import InputControl from '../InputControl/InputControl';
import { Dispatch, RootState } from '../../store/store';
import { connect } from 'react-redux';
import { CycleAttachment as CycleAttachmentType } from '../../store/models/cycles';
import IconBin from '../Icons/IconBin/IconBin';
import Modal from '../Modal/Modal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { OpenReport } from '../Icons/OpenReport/OpenReport';
import { ElementSize } from '../../types/element-size';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CycleAttachmentProps extends ReturnType<typeof mapState>, ReturnType<typeof mapDispatch> {
  onSelectFile: (file: File) => void,
}

const CycleAttachment: React.FC<CycleAttachmentProps> = ({ onSelectFile, attachments, deleteById }) => {
  const { t } = useTranslation();

  const [cycleAttachment, setCycleAttachment] = useState<CycleAttachmentType | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onSelectFile(file);
    }
  };

  const openDeletionModal = (data: CycleAttachmentType) => {
    setCycleAttachment(data);
  };

  const closeDeletionModal = () => {
    setCycleAttachment(null);
  };

  const deleteAttachment = async () => {
    try {
      await deleteById({ attachmentId: cycleAttachment.id, cycleId: cycleAttachment.cycleId });
    } finally {
      setCycleAttachment(null);
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4 c-cycle-attachment">
        <InputControl
          type="file"
          label="Upload Attachment"
          accept=".pdf,application/pdf"
          onChange={handleFileSelect}
        />

        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Attachments</h3>
          {attachments.length === 0 ? (
            <p className="text-gray-500">No attachments uploaded</p>
          ) : (
            <ul className="flex flex-col gap-y-2">
              {attachments.map(attachment => (
                <li key={attachment.id} className="flex flex-row gap-x-2 justify-between py-2 px-3 border rounded text-left">
                  <span className='inline-block flex-auto'>{attachment.name || 'View Attachment'}</span>
                  <InteractiveButton className='hover:bg-white min-w-6 bg-white border-foggy-mist' size={ElementSize.XXS} as='a' target="_blank" rel="noopener noreferrer" href={attachment.attachment} iconLeft={<OpenReport stroke='inherit' iconClassName='stroke-black' />} />
                  <InteractiveButton onClick={() => openDeletionModal(attachment)} className='hover:bg-white min-w-6 bg-white border-foggy-mist' size={ElementSize.XXS} iconLeft={<IconBin iconClassName='text-red cursor-pointer' />} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Modal
        modalTitle={t('cycle.modals.attachments.delete.title')}
        isVisible={Boolean(cycleAttachment)}
        content={t('cycle.modals.attachments.delete.description', '', { name: cycleAttachment?.name })}
        acceptBtnText={t('buttons.delete')}
        onClose={closeDeletionModal}
        onAccept={deleteAttachment}
        showAcceptBtn
        showCancelBtn
      />
    </>
  );
};

const mapState = (state: RootState) => ({
  attachments: state.cycles.attachments,
});

const mapDispatch = (dispatch: Dispatch) => ({
  deleteById: dispatch.cycles.deleteAttachment,
});

export default connect(mapState, mapDispatch)(CycleAttachment);